import React, { useRef, useState, useCallback } from "react";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import PianoIcon from "@mui/icons-material/Piano";
import { useDAW } from "./DAWProvider";
import TimelineRuler, { PIXELS_PER_BEAT } from "./TimelineRuler";
import ArrangementTrack from "./ArrangementTrack";

const AUDIO_ACCEPT = ".wav,.mp3,.ogg,.m4a,.aac,.flac,.webm";
const HEADER_WIDTH = 160;

export default function Arrangement() {
  const { arrangementBars, bpm, isPlaying, playheadPosition, audioTracks, instrumentTracks, importAudioFile, addInstrumentTrack } = useDAW();
  const fileInputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const beatsPerBar = 4;
  const totalBeats = arrangementBars * beatsPerBar;
  const totalWidth = totalBeats * PIXELS_PER_BEAT;
  const totalDuration = arrangementBars * (60 / bpm) * 4;
  const playheadX = totalDuration > 0 ? (playheadPosition / totalDuration) * totalWidth : 0;

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files).filter((f) =>
      f.type.startsWith("audio/")
    );

    for (const file of files) {
      const rect = e.currentTarget.getBoundingClientRect();
      const dropX = e.clientX - rect.left - HEADER_WIDTH;
      const pxPerSec = totalDuration > 0 ? totalWidth / totalDuration : 1;
      const startTime = Math.max(0, dropX / pxPerSec);
      await importAudioFile(file, startTime);
    }
  }, [importAudioFile, totalWidth, totalDuration]);

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      await importAudioFile(file);
    }
    e.target.value = "";
  };

  const gridWidth = HEADER_WIDTH + totalWidth;

  return (
    <div
      className={`daw-arrangement${isDragOver ? " daw-arrangement--drop-active" : ""}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="daw-arrangement__scroll">
        <div className="daw-arrangement__grid" style={{ minWidth: gridWidth }}>
          {/* Ruler row */}
          <div className="daw-arrangement__ruler-row">
            <div className="daw-arrangement__corner">
              <IconButton
                size="small"
                onClick={handleImportClick}
                sx={{ color: "#888", "&:hover": { color: "#EE772F" } }}
                title="Import audio file"
              >
                <AddIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => addInstrumentTrack()}
                sx={{ color: "#888", "&:hover": { color: "#ba68c8" } }}
                title="Add instrument track"
              >
                <PianoIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <input
                ref={fileInputRef}
                type="file"
                accept={AUDIO_ACCEPT}
                multiple
                style={{ display: "none" }}
                onChange={handleFileSelect}
              />
            </div>
            <div className="daw-arrangement__ruler-area">
              <TimelineRuler />
            </div>
          </div>

          {/* Track rows */}
          <div className="daw-arrangement__track-row">
            <ArrangementTrack type="drums" />
          </div>
          {instrumentTracks.map((track) => (
            <div key={`inst-${track.id}`} className="daw-arrangement__track-row">
              <ArrangementTrack type="instrument" track={track} />
            </div>
          ))}
          {audioTracks.map((track) => (
            <div key={track.id} className="daw-arrangement__track-row">
              <ArrangementTrack type="audio" track={track} />
            </div>
          ))}
        </div>

        {/* Playhead overlay — inside scroll so it scrolls with content */}
        {isPlaying && (
          <div
            className="daw-playhead"
            style={{ left: HEADER_WIDTH + playheadX }}
          />
        )}
      </div>

      {/* Drop zone indicator */}
      {isDragOver && (
        <div className="daw-arrangement__drop-hint">
          Drop audio files here
        </div>
      )}
    </div>
  );
}
